import type React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Loader2, Clock, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

export type ResearchStage =
  | 'query_generation'
  | 'source_collection'
  | 'url_validation'
  | 'content_processing'
  | 'indexing'
  | 'planning'
  | 'writing';

export type StageStatus = 'pending' | 'running' | 'complete' | 'error';

export interface ResearchProgress {
  stage: ResearchStage;
  status: StageStatus;
  message?: string;
  details?: string;
  progress?: number;
  error?: string;
}

interface ResearchProgressTrackerProps {
  currentStage: ResearchStage;
  stages: Record<ResearchStage, StageStatus>;
  messages: Record<ResearchStage, string>;
  error?: string;
  sourcesOnly?: boolean;
}

const STAGE_CONFIG: Record<
  ResearchStage,
  { label: string; description: string }
> = {
  query_generation: {
    label: 'Query Generation',
    description: 'Generating search queries for comprehensive research'
  },
  source_collection: {
    label: 'Source Collection',
    description: 'Finding relevant sources from the web'
  },
  url_validation: {
    label: 'URL Validation',
    description: 'Validating and checking source accessibility'
  },
  content_processing: {
    label: 'Content Processing',
    description: 'Extracting and processing content from sources'
  },
  indexing: {
    label: 'Indexing',
    description: 'Creating semantic indexes for retrieval'
  },
  planning: {
    label: 'Outline Planning',
    description: 'Generating article outline and structure'
  },
  writing: {
    label: 'Article Writing',
    description: 'Writing the final article'
  }
};

const getStatusIcon = (status: StageStatus) => {
  switch (status) {
    case 'pending':
      return <Clock className="h-5 w-5 text-muted-foreground" />;
    case 'running':
      return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    case 'complete':
      return <CheckCircle2 className="h-5 w-5 text-primary" />;
    case 'error':
      return <AlertCircle className="h-5 w-5 text-destructive" />;
  }
};

export const ResearchProgressTracker: React.FC<ResearchProgressTrackerProps> = ({
  currentStage,
  stages,
  messages,
  error,
  sourcesOnly = false
}) => {
  const fullStageOrder: ResearchStage[] = [
    'query_generation',
    'source_collection',
    'url_validation',
    'content_processing',
    'indexing',
    'planning',
    'writing'
  ];

  const sourcesOnlyStageOrder: ResearchStage[] = [
    'query_generation',
    'source_collection',
    'url_validation',
    'content_processing'
  ];

  const stageOrder = sourcesOnly ? sourcesOnlyStageOrder : fullStageOrder;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Research Progress</span>
          {error && (
            <span className="text-sm font-normal text-destructive">
              Research Failed
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Error occurred</p>
                <p className="text-sm text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {stageOrder.map((stage, index) => {
            const status = stages[stage];
            const config = STAGE_CONFIG[stage];
            const message = messages[stage];
            const isActive = stage === currentStage && status === 'running';

            return (
              <div
                key={stage}
                className={cn(
                  'p-4 rounded-lg border-2 transition-all',
                  isActive && 'border-primary bg-primary/5',
                  !isActive && status === 'complete' && 'border-primary/30',
                  !isActive && status === 'pending' && 'border-muted',
                  status === 'error' && 'border-destructive bg-destructive/5'
                )}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4
                        className={cn(
                          'font-medium',
                          isActive && 'text-primary',
                          status === 'complete' && 'text-primary',
                          status === 'error' && 'text-destructive'
                        )}
                      >
                        {index + 1}. {config.label}
                      </h4>
                      {status === 'running' && (
                        <span className="text-xs text-muted-foreground">
                          In progress...
                        </span>
                      )}
                      {status === 'complete' && (
                        <span className="text-xs text-primary">Complete</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {message || config.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!error && (sourcesOnly ? stages.content_processing !== 'complete' : stages.writing !== 'complete') && (
          <div className="text-center text-sm text-muted-foreground pt-2">
            {sourcesOnly
              ? 'Collecting and validating sources...'
              : 'This process may take several minutes...'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
